import { api } from "./client"

export type ApplicationStatus = "active" | "rejected" | "hired" | "withdrawn"

export type ApplicationListItem = {
  id: string
  organization_id: string
  job_id: string
  candidate_id: string
  current_stage_id: string | null
  status: ApplicationStatus
  applied_at: string
  candidate: { id: string; full_name: string; email: string }
  job: { id: string; title: string }
  analysis: { match_score: number | null } | null
}

export type ApplicationDetail = ApplicationListItem & {
  resume_id: string | null
  cover_letter: string | null
  resume: {
    id: string
    file_path: string
    file_name: string
    mime_type: string
    size_bytes: number | null
    uploaded_at: string
  } | null
  candidate: {
    id: string
    full_name: string
    email: string
    phone: string | null
    linkedin_url: string | null
    location: string | null
    tags: string[]
    notes: string | null
  }
  job: { id: string; title: string; slug: string; status: string }
  analysis: {
    id: string
    match_score: number | null
    strengths: string[] | null
    weaknesses: string[] | null
    summary: string | null
    skills_extracted: string[] | null
    experience_years: number | null
    model_used: string | null
    created_at: string
  } | null
  history: {
    id: string
    stage_id: string | null
    moved_by: string | null
    moved_at: string
    note: string | null
  }[]
}

export const applicationsApi = {
  list: (jobId?: string) =>
    api<{ data: ApplicationListItem[] }>(
      `/applications${jobId ? `?job_id=${jobId}` : ""}`,
    ),
  get: (id: string) => api<{ data: ApplicationDetail }>(`/applications/${id}`),
  resumeUrl: (id: string) =>
    api<{ url: string; file_name: string; mime_type: string }>(
      `/applications/${id}/resume-url`,
    ),
  analyze: (id: string) =>
    api<{
      ok: boolean
      match_score: number | null
      model: string
      usage: { input: number | null; output: number | null }
    }>(`/applications/${id}/analyze`, { method: "POST" }),
  interviewQuestions: (id: string) =>
    api<{
      ok: boolean
      questions: { technical: string[]; behavioral: string[] }
      model: string
    }>(`/applications/${id}/interview-questions`, {
      method: "POST",
    }),
  analyzeBatch: (jobId: string, limit = 10) =>
    api<{
      ok: boolean
      total: number
      success: number
      failed: number
      results: {
        application_id: string
        ok: boolean
        match_score?: number | null
        error?: string
      }[]
    }>(`/ai/analyze-batch?job_id=${jobId}&limit=${limit}`, { method: "POST" }),
}
